/* GASSISA
=========================================================================================
* Descripción: Laboratorio de configuración de plantillas de ticket por caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Creación de laboratorio master de tickets
*      2       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Editor visual de plantilla con objetos JSON arrastrables
*      3       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Homologación de carga resolviendo caja canónica
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import PreviewIcon from "@mui/icons-material/Preview";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TableRowsIcon from "@mui/icons-material/TableRows";
import TextFieldsIcon from "@mui/icons-material/TextFields";

import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import MnuParametrosCajaActions from "./MnuParametrosCajaActions";
import { useParametros } from "../../context/Parametros/ParametrosContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";

const TICKET_UNIT_PX = 11;
const TICKET_WIDTH_UNITS = 32;

const defaultTicketJson = {
  version: 2,
  tipoticket: "VENTA",
  anchoColumnas: 32,
  modo: "visual",
  elementos: [
    { id: "encabezado", tipo: "texto", origen: "encabezado", titulo: "Encabezado", visible: true, x: 0, y: 1, ancho: 32, alto: 7, texto: "{{encabezado}}", fuente: { alineacion: "center", negrita: false, tamano: "normal" } },
    { id: "datosventa", tipo: "texto", origen: "datosventa", titulo: "Datos de venta", visible: true, x: 0, y: 9, ancho: 32, alto: 11, texto: "Caja: {{caja}}\nRemision: {{remision}}\nVendedor: {{usuario}}\nFecha: {{fechaventa}}\nTipo de Pago: {{tipopago}}\nCliente: {{cliente}}", fuente: { alineacion: "left", negrita: false, tamano: "normal" } },
    { id: "partidas", tipo: "tablaPartidas", origen: "partidas", titulo: "Partidas", visible: true, x: 0, y: 22, ancho: 32, alto: 10, texto: "{{partidas}}", fuente: { alineacion: "left", negrita: false, tamano: "normal" } },
    { id: "totales", tipo: "texto", origen: "totales", titulo: "Totales", visible: true, x: 0, y: 34, ancho: 32, alto: 9, texto: "Subtotal: {{subtotal}}\nIVA: {{iva}}\nTotal: {{total}}\nEfectivo: {{totalefectivo}}\nCambio: {{totalcambio}}", fuente: { alineacion: "right", negrita: true, tamano: "normal" } },
    { id: "politicas", tipo: "texto", origen: "politicas", titulo: "Políticas", visible: true, x: 0, y: 45, ancho: 32, alto: 11, texto: "POLÍTICA DE CAMBIOS (15 DÍAS)\nTicket físico indispensable.\nArtículos sin uso, con etiquetas y empaque original.\nNO SE ACEPTAN DEVOLUCIONES.", fuente: { alineacion: "center", negrita: false, tamano: "normal" } },
    { id: "pie", tipo: "texto", origen: "pie", titulo: "Pie", visible: true, x: 0, y: 58, ancho: 32, alto: 7, texto: "{{pie}}\nVuelva pronto", fuente: { alineacion: "center", negrita: false, tamano: "normal" } },
  ],
};

const sampleData = {
  encabezado: "ALEJANDRO FERNANDEZ CASTILLA\n=========*APIZACO*============\nRFC: FECA7407224X6",
  caja: "LAPARAGONGASSISA",
  remision: "1005",
  usuario: "sistemas",
  fechaventa: "03/06/2026 18:30",
  tipopago: "CONTADO",
  cliente: "PUBLICO EN GENERAL",
  partidas: "1  099 ZAPATO NIÑO BEIGE-NEGRO\n   11.5 x $565.00\n2  2503 CARAMEL CHAROL BLANCO\n   13 x $480.00",
  subtotal: "$1,052.59",
  iva: "$168.41",
  total: "$1,221.00",
  totalefectivo: "$1,300.00",
  totalcambio: "$79.00",
  pie: "==============================\n   GRACIAS POR SU PREFERENCIA",
};

const prettyDefault = () => JSON.stringify(defaultTicketJson, null, 2);
const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const fieldCatalog = [
  { id: "encabezado", label: "Encabezado", token: "{{encabezado}}", tipo: "texto" },
  { id: "caja", label: "Caja", token: "Caja: {{caja}}", tipo: "texto" },
  { id: "remision", label: "Remisión", token: "Remision: {{remision}}", tipo: "texto" },
  { id: "usuario", label: "Vendedor", token: "Vendedor: {{usuario}}", tipo: "texto" },
  { id: "fechaventa", label: "Fecha", token: "Fecha: {{fechaventa}}", tipo: "texto" },
  { id: "tipopago", label: "Tipo pago", token: "Tipo de Pago: {{tipopago}}", tipo: "texto" },
  { id: "cliente", label: "Cliente", token: "Cliente: {{cliente}}", tipo: "texto" },
  { id: "partidas", label: "Partidas", token: "{{partidas}}", tipo: "tablaPartidas" },
  { id: "subtotal", label: "Subtotal", token: "Subtotal: {{subtotal}}", tipo: "texto" },
  { id: "iva", label: "IVA", token: "IVA: {{iva}}", tipo: "texto" },
  { id: "total", label: "Total", token: "Total: {{total}}", tipo: "texto" },
  { id: "totalefectivo", label: "Efectivo", token: "Efectivo: {{totalefectivo}}", tipo: "texto" },
  { id: "totalcambio", label: "Cambio", token: "Cambio: {{totalcambio}}", tipo: "texto" },
  { id: "pie", label: "Pie", token: "{{pie}}", tipo: "texto" },
];

const fontSizeMap = {
  chico: 10,
  normal: 12,
  grande: 15,
  doble: 18,
};

const parsePlantilla = (text) => {
  const parsed = JSON.parse(text || "{}");
  if (Array.isArray(parsed?.elementos)) return parsed;
  if (Array.isArray(parsed?.bloques)) return parsed;
  return { ...parsed, elementos: [] };
};

const replaceTokens = (text = "") =>
  String(text).replace(/\{\{(.*?)\}\}/g, (_, key) => sampleData[String(key).trim().toLowerCase()] ?? "");

const normalizeElement = (elemento, index = 0) => ({
  id: String(elemento?.id || `elemento_${Date.now()}_${index}`),
  tipo: elemento?.tipo || "texto",
  origen: elemento?.origen || elemento?.id || "manual",
  titulo: elemento?.titulo || elemento?.origen || elemento?.id || "Elemento",
  visible: elemento?.visible !== false,
  x: Math.max(0, Number(elemento?.x ?? 0)),
  y: Math.max(0, Number(elemento?.y ?? index * 7)),
  ancho: Math.max(4, Number(elemento?.ancho ?? TICKET_WIDTH_UNITS)),
  alto: Math.max(2, Number(elemento?.alto ?? 5)),
  texto: elemento?.texto ?? elemento?.contenido ?? "",
  imagenBase64: elemento?.imagenBase64 || "",
  fuente: {
    alineacion: elemento?.fuente?.alineacion || elemento?.alineacion || "left",
    negrita: Boolean(elemento?.fuente?.negrita ?? elemento?.negrita ?? false),
    tamano: elemento?.fuente?.tamano || elemento?.tamano || "normal",
  },
});

const convertBloquesToVisual = (plantilla) => ({
  version: 2,
  tipoticket: plantilla?.tipoticket || "VENTA",
  anchoColumnas: Number(plantilla?.anchoColumnas ?? TICKET_WIDTH_UNITS),
  modo: "visual",
  elementos: (plantilla?.bloques ?? [])
    .sort((a, b) => Number(a?.orden ?? 0) - Number(b?.orden ?? 0))
    .map((bloque, index) =>
      normalizeElement(
        {
          id: bloque?.id,
          tipo: bloque?.id === "partidas" ? "tablaPartidas" : "texto",
          origen: bloque?.id,
          titulo: bloque?.titulo,
          visible: bloque?.visible,
          x: 0,
          y: index * 8 + 1,
          ancho: Number(plantilla?.anchoColumnas ?? TICKET_WIDTH_UNITS),
          alto: bloque?.id === "partidas" ? 10 : 6,
          texto: bloque?.contenido,
          fuente: {
            alineacion: bloque?.alineacion || "left",
            negrita: Boolean(bloque?.negrita),
            tamano: bloque?.tamano || "normal",
          },
        },
        index
      )
    ),
});

const normalizeEditorSchema = (text) => {
  const parsed = parsePlantilla(text);
  if (Array.isArray(parsed?.bloques) && !Array.isArray(parsed?.elementos)) {
    return convertBloquesToVisual(parsed);
  }

  const base = {
    ...defaultTicketJson,
    ...parsed,
    version: Number(parsed?.version ?? 2),
    tipoticket: parsed?.tipoticket || "VENTA",
    anchoColumnas: Number(parsed?.anchoColumnas ?? TICKET_WIDTH_UNITS),
    modo: "visual",
  };

  const elementos = Array.isArray(parsed?.elementos)
    ? parsed.elementos.map((elemento, index) => normalizeElement(elemento, index))
    : defaultTicketJson.elementos;

  return { ...base, elementos };
};

const stringifySchema = (schema) => JSON.stringify(schema, null, 2);

const getNextY = (schema) =>
  Math.max(1, ...((schema?.elementos ?? []).map((elemento) => Number(elemento.y || 0) + Number(elemento.alto || 0) + 2)));

const renderElementText = (elemento) => {
  if (elemento?.tipo === "linea") return "------------------------------";
  return replaceTokens(elemento?.texto);
};

const buildPreview = (text) => {
  const parsed = parsePlantilla(text);
  const schema = Array.isArray(parsed?.elementos) ? normalizeEditorSchema(text) : convertBloquesToVisual(parsed);

  return schema.elementos
    .filter((elemento) => elemento?.visible !== false)
    .sort((a, b) => Number(a?.y ?? 0) - Number(b?.y ?? 0) || Number(a?.x ?? 0) - Number(b?.x ?? 0))
    .map((elemento) => (elemento?.tipo === "imagen" ? "[IMAGEN]" : renderElementText(elemento)))
    .join("\n");
};

export default function FrmParametrosCajaFormMaster() {
  const { isLocked: isFormLocked } = useFormTabLock("parametros_ticket_master", {
    title: "Laboratorio Ticket Master",
  });

  const {
    handleClickRegresarMainDashboard,
    handleobtenerListaEquipos,
    handleObtenerParametrosCaja,
    handleObtenerParametrosTicket,
    handleSaveParametrosTicket,
    listaEquipos,
  } = useParametros();

  const { handleOpenAlert } = useLayout();
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [parametrosCaja, setParametrosCaja] = useState(null);
  const [serverTicket, setServerTicket] = useState(null);
  const [draftTicket, setDraftTicket] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [localError, setLocalError] = useState("");
  const [editorSchema, setEditorSchema] = useState(defaultTicketJson);
  const [selectedElementId, setSelectedElementId] = useState(defaultTicketJson.elementos[0].id);
  const [dragState, setDragState] = useState(null);
  const imageInputRef = useRef(null);

  const selectedElement = useMemo(
    () => editorSchema?.elementos?.find((elemento) => elemento.id === selectedElementId) ?? null,
    [editorSchema, selectedElementId]
  );

  const paperHeight = useMemo(() => {
    const maxBottom = Math.max(70, ...((editorSchema?.elementos ?? []).map((elemento) => Number(elemento.y || 0) + Number(elemento.alto || 0) + 4)));
    return maxBottom * TICKET_UNIT_PX;
  }, [editorSchema]);

  const isDirty = useMemo(() => {
    if (!serverTicket || !draftTicket) return false;
    return !deepEqual(serverTicket, draftTicket);
  }, [serverTicket, draftTicket]);

  const equiposUnicos = useMemo(() => {
    const map = new Map();
    (listaEquipos ?? []).forEach((equipo) => {
      const id = String(equipo?.idpc ?? equipo?.idPC ?? equipo?.IdPc ?? equipo?.guid ?? equipo?.id ?? "").trim().toUpperCase();
      const pc = String(equipo?.pc ?? equipo?.nombre ?? equipo?.caja ?? equipo?.descripcion ?? "").trim().toUpperCase();
      const key = `${id}|${pc}`;
      if (!map.has(key)) map.set(key, equipo);
    });
    return [...map.values()];
  }, [listaEquipos]);

  useEffect(() => {
    window.scrollTo(0, 0);
    handleobtenerListaEquipos();
  }, []);

  useEffect(() => {
    if (!isFormLocked) return;
    handleOpenAlert("El laboratorio de tickets ya está abierto en otra pestaña.", "error");
    setTimeout(() => handleClickRegresarMainDashboard(), 900);
  }, [isFormLocked, handleClickRegresarMainDashboard, handleOpenAlert]);

  useEffect(() => {
    if (!dragState) return undefined;

    const handleMove = (event) => {
      const deltaX = Math.round((event.clientX - dragState.startClientX) / TICKET_UNIT_PX);
      const deltaY = Math.round((event.clientY - dragState.startClientY) / TICKET_UNIT_PX);
      const nextX = Math.max(0, Math.min(TICKET_WIDTH_UNITS - dragState.ancho, dragState.originX + deltaX));
      const nextY = Math.max(0, dragState.originY + deltaY);

      updateElement(dragState.id, { x: nextX, y: nextY });
    };

    const handleUp = () => setDragState(null);
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [dragState]);

  const getEquipoLabel = (e) => {
    if (!e) return "";
    return String(e.pc ?? e.nombre ?? e.caja ?? e.descripcion ?? e.idpc ?? "").trim();
  };

  const getEquipoId = (e) => {
    if (!e) return "";
    return String(e.idpc ?? e.idPC ?? e.IdPc ?? e.guid ?? e.id ?? "").trim();
  };

  const loadTicket = async (equipo) => {
    const idCajaEquipo = getEquipoId(equipo).trim();
    const nombreEquipo = getEquipoLabel(equipo).trim().toUpperCase();
    const cajaFiltro = idCajaEquipo || nombreEquipo;
    if (!cajaFiltro) return;

    setLocalError("");
    const caja = await handleObtenerParametrosCaja(idCajaEquipo);
    const ticket = await handleObtenerParametrosTicket((caja?.idcaja || cajaFiltro).trim().toUpperCase(), "VENTA");
    if (!ticket) return;

    const idCaja = (ticket.idcaja || caja?.idcaja || cajaFiltro).trim().toUpperCase();
    const nombreCaja = (ticket.caja || caja?.caja || nombreEquipo).trim().toUpperCase();
    const normalizedTicket = {
      ...ticket,
      idcaja: idCaja,
      caja: nombreCaja,
      tipoticket: "VENTA",
      plantillajson: ticket.plantillajson || prettyDefault(),
    };
    const schema = normalizeEditorSchema(normalizedTicket.plantillajson);
    const ticketVisual = { ...normalizedTicket, plantillajson: stringifySchema(schema) };

    setParametrosCaja(caja);
    setEditorSchema(schema);
    setSelectedElementId(schema.elementos?.[0]?.id ?? null);
    setServerTicket(ticketVisual);
    setDraftTicket(ticketVisual);
  };

  const setField = (field, value) => {
    setDraftTicket((prev) => ({ ...prev, [field]: value }));
  };

  const applyEditorSchema = (nextSchema) => {
    const normalized = {
      ...nextSchema,
      version: Number(nextSchema?.version ?? 2),
      tipoticket: nextSchema?.tipoticket || "VENTA",
      anchoColumnas: Number(nextSchema?.anchoColumnas ?? TICKET_WIDTH_UNITS),
      modo: "visual",
      elementos: (nextSchema?.elementos ?? []).map((elemento, index) => normalizeElement(elemento, index)),
    };

    setEditorSchema(normalized);
    setDraftTicket((prev) => (prev ? { ...prev, plantillajson: stringifySchema(normalized) } : prev));
  };

  const updateElement = (id, changes) => {
    setEditorSchema((prev) => {
      const next = {
        ...prev,
        elementos: prev.elementos.map((elemento) =>
          elemento.id === id ? normalizeElement({ ...elemento, ...changes }, 0) : elemento
        ),
      };
      setDraftTicket((current) => (current ? { ...current, plantillajson: stringifySchema(next) } : current));
      return next;
    });
  };

  const updateSelectedElement = (changes) => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, changes);
  };

  const addElement = (field) => {
    const nextElement = normalizeElement({
      id: `${field.id}_${Date.now()}`,
      tipo: field.tipo || "texto",
      origen: field.id,
      titulo: field.label,
      visible: true,
      x: 0,
      y: getNextY(editorSchema),
      ancho: TICKET_WIDTH_UNITS,
      alto: field.tipo === "tablaPartidas" ? 10 : 4,
      texto: field.token,
      fuente: { alineacion: field.id === "total" ? "right" : "left", negrita: field.id === "total", tamano: "normal" },
    });

    const next = { ...editorSchema, elementos: [...editorSchema.elementos, nextElement] };
    applyEditorSchema(next);
    setSelectedElementId(nextElement.id);
  };

  const addLine = () => {
    const nextElement = normalizeElement({
      id: `linea_${Date.now()}`,
      tipo: "linea",
      origen: "linea",
      titulo: "Línea separadora",
      visible: true,
      x: 0,
      y: getNextY(editorSchema),
      ancho: TICKET_WIDTH_UNITS,
      alto: 2,
      texto: "",
      fuente: { alineacion: "center", negrita: false, tamano: "normal" },
    });
    const next = { ...editorSchema, elementos: [...editorSchema.elementos, nextElement] };
    applyEditorSchema(next);
    setSelectedElementId(nextElement.id);
  };

  const convertImageToBlackWhite = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => {
          const maxWidth = 320;
          const scale = Math.min(1, maxWidth / image.width);
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(image.width * scale));
          canvas.height = Math.max(1, Math.round(image.height * scale));
          const ctx = canvas.getContext("2d");
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          for (let i = 0; i < data.length; i += 4) {
            const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
            const bw = gray > 150 ? 255 : 0;
            data[i] = bw;
            data[i + 1] = bw;
            data[i + 2] = bw;
          }
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = reject;
        image.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onImageSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const imagenBase64 = await convertImageToBlackWhite(file);
      const nextElement = normalizeElement({
        id: `imagen_${Date.now()}`,
        tipo: "imagen",
        origen: "imagen",
        titulo: "Imagen",
        visible: true,
        x: 6,
        y: getNextY(editorSchema),
        ancho: 20,
        alto: 12,
        texto: "",
        imagenBase64,
        fuente: { alineacion: "center", negrita: false, tamano: "normal" },
      });
      const next = { ...editorSchema, elementos: [...editorSchema.elementos, nextElement] };
      applyEditorSchema(next);
      setSelectedElementId(nextElement.id);
    } catch (error) {
      handleOpenAlert(`No fue posible cargar la imagen: ${error.message}`, "error");
    }
  };

  const onJsonBlur = () => {
    if (!validateJson()) return;
    try {
      const schema = normalizeEditorSchema(draftTicket?.plantillajson);
      applyEditorSchema(schema);
      setSelectedElementId(schema.elementos?.[0]?.id ?? null);
    } catch (error) {
      setLocalError(`JSON invélido: ${error.message}`);
    }
  };

  const validateJson = () => {
    try {
      parsePlantilla(draftTicket?.plantillajson);
      setLocalError("");
      return true;
    } catch (error) {
      setLocalError(`JSON invélido: ${error.message}`);
      return false;
    }
  };

  const onPreview = () => {
    if (!draftTicket || !validateJson()) return;
    setPreviewText(buildPreview(draftTicket.plantillajson));
    setPreviewOpen(true);
  };

  const onSave = async () => {
    if (!draftTicket || !validateJson()) return;
    const saved = await handleSaveParametrosTicket(draftTicket);
    if (!saved) return;

    setServerTicket(saved);
    setDraftTicket(saved);
    window.scrollTo(0, 0);
  };

  const onDeshacer = () => {
    setSelectedEquipo(null);
    setParametrosCaja(null);
    setServerTicket(null);
    setDraftTicket(null);
    setLocalError("");
  };

  const onRestoreDefault = () => {
    if (!draftTicket) return;
    applyEditorSchema(defaultTicketJson);
    setSelectedElementId(defaultTicketJson.elementos[0].id);
    setLocalError("");
  };

  const renderTicketElement = (elemento) => {
    const selected = elemento.id === selectedElementId;
    const fontSize = fontSizeMap[elemento.fuente?.tamano] ?? fontSizeMap.normal;
    const commonStyle = {
      position: "absolute",
      left: elemento.x * TICKET_UNIT_PX,
      top: elemento.y * TICKET_UNIT_PX,
      width: elemento.ancho * TICKET_UNIT_PX,
      minHeight: elemento.alto * TICKET_UNIT_PX,
      border: selected ? "2px solid #1976d2" : "1px dashed #b0bec5",
      bgcolor: elemento.visible ? "rgba(255,255,255,0.92)" : "rgba(245,245,245,0.65)",
      opacity: elemento.visible ? 1 : 0.55,
      cursor: "move",
      overflow: "hidden",
      p: 0.5,
      fontFamily: "Consolas, monospace",
      fontSize,
      fontWeight: elemento.fuente?.negrita ? 700 : 400,
      textAlign: elemento.fuente?.alineacion || "left",
      whiteSpace: "pre-wrap",
      userSelect: "none",
      boxShadow: selected ? "0 0 0 2px rgba(25,118,210,0.18)" : "none",
    };

    return (
      <Box
        key={elemento.id}
        sx={commonStyle}
        onMouseDown={(event) => {
          event.preventDefault();
          setSelectedElementId(elemento.id);
          setDragState({
            id: elemento.id,
            startClientX: event.clientX,
            startClientY: event.clientY,
            originX: elemento.x,
            originY: elemento.y,
            ancho: elemento.ancho,
          });
        }}
      >
        <Typography variant="caption" sx={{ display: "block", color: selected ? "primary.main" : "text.secondary", fontFamily: "inherit" }}>
          {elemento.titulo}
        </Typography>
        {elemento.tipo === "imagen" && elemento.imagenBase64 ? (
          <Box component="img" src={elemento.imagenBase64} alt={elemento.titulo} sx={{ width: "100%", height: "auto", display: "block" }} />
        ) : (
          renderElementText(elemento)
        )}
      </Box>
    );
  };

  return (
    <SizeSoftXLayoutForm
      titlePage="Ticket ventas master"
      buttonActions={
        <MnuParametrosCajaActions
          handleClickRegresarMainDashboard={handleClickRegresarMainDashboard}
          hasDraft={!!draftTicket}
          isDirty={isDirty}
          onSave={onSave}
          onDeshacer={onDeshacer}
        />
      }
    >
      <Box sx={{ width: "100%", p: 1 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Laboratorio de formato de ticket
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta pantalla prepara plantillas por caja sin modificar el ticket actual de ventas.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {localError && <Alert severity="error" sx={{ mb: 2 }}>{localError}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={equiposUnicos}
                  value={selectedEquipo}
                  onChange={(_, val) => {
                    setSelectedEquipo(val);
                    if (val) loadTicket(val);
                  }}
                  getOptionLabel={getEquipoLabel}
                  isOptionEqualToValue={(opt, val) => getEquipoId(opt) === getEquipoId(val)}
                  getOptionKey={(option) => `${getEquipoId(option)}-${getEquipoLabel(option)}`}
                  renderInput={(params) => <TextField {...params} label="Caja / equipo" size="small" />}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", minHeight: 40, alignItems: "center" }}>
                  {parametrosCaja?.caja && <Chip label={`Caja: ${parametrosCaja.caja}`} />}
                  {draftTicket?.version && <Chip label={`Versión: ${draftTicket.version}`} />}
                  {draftTicket && (isDirty ? <Chip color="warning" label="Cambios pendientes" /> : <Chip color="success" label="Sin cambios" />)}
                </Box>
              </Grid>
            </Grid>

            {draftTicket && (
              <>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField autoComplete="off"
                      label="Nombre plantilla"
                      size="small"
                      fullWidth
                      value={draftTicket.nombreplantilla ?? ""}
                      onChange={(e) => setField("nombreplantilla", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField autoComplete="off" label="Tipo ticket" size="small" fullWidth value={draftTicket.tipoticket ?? "VENTA"} disabled />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField autoComplete="off" label="Activa" size="small" fullWidth value={Number(draftTicket.activa ?? 1) === 1 ? "Sí" : "No"} disabled />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 1, flexWrap: "wrap" }}>
                      <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onRestoreDefault}>
                        Restaurar default
                      </Button>
                      <Button variant="contained" startIcon={<PreviewIcon />} onClick={onPreview}>
                        Vista previa
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12} lg={3}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          Campos disponibles
                        </Typography>
                        <Stack spacing={1}>
                          {fieldCatalog.map((field) => (
                            <Button
                              key={field.id}
                              variant="outlined"
                              size="small"
                              startIcon={field.tipo === "tablaPartidas" ? <TableRowsIcon /> : <TextFieldsIcon />}
                              onClick={() => addElement(field)}
                              sx={{ justifyContent: "flex-start" }}
                            >
                              {field.label}
                            </Button>
                          ))}
                          <Button variant="outlined" size="small" startIcon={<HorizontalRuleIcon />} onClick={addLine} sx={{ justifyContent: "flex-start" }}>
                            Línea separadora
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddPhotoAlternateIcon />}
                            onClick={() => imageInputRef.current?.click()}
                            sx={{ justifyContent: "flex-start" }}
                          >
                            Imagen B/N
                          </Button>
                          <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={onImageSelected} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <Box sx={{ display: "flex", justifyContent: "center", overflowX: "auto", pb: 1 }}>
                      <Box
                        sx={{
                          width: TICKET_WIDTH_UNITS * TICKET_UNIT_PX,
                          minHeight: paperHeight,
                          position: "relative",
                          bgcolor: "#fff",
                          border: "1px solid #d7d7d7",
                          boxShadow: "0 10px 28px rgba(0,0,0,0.16)",
                          backgroundImage: "linear-gradient(#f4f6f8 1px, transparent 1px), linear-gradient(90deg, #f4f6f8 1px, transparent 1px)",
                          backgroundSize: `${TICKET_UNIT_PX}px ${TICKET_UNIT_PX}px`,
                        }}
                      >
                        {editorSchema.elementos.map(renderTicketElement)}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          Propiedades
                        </Typography>
                        {!selectedElement ? (
                          <Alert severity="info">Seleccione un objeto de la plantilla.</Alert>
                        ) : (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                              <TextField autoComplete="off"
                                label="Título"
                                size="small"
                                fullWidth
                                value={selectedElement.titulo}
                                onChange={(e) => updateSelectedElement({ titulo: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                  label="Tipo"
                                  value={selectedElement.tipo}
                                  onChange={(e) => updateSelectedElement({ tipo: e.target.value })}
                                >
                                  <MenuItem value="texto">Texto</MenuItem>
                                  <MenuItem value="tablaPartidas">Partidas</MenuItem>
                                  <MenuItem value="linea">Línea</MenuItem>
                                  <MenuItem value="imagen">Imagen</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Alineación</InputLabel>
                                <Select
                                  label="Alineación"
                                  value={selectedElement.fuente.alineacion}
                                  onChange={(e) => updateSelectedElement({ fuente: { ...selectedElement.fuente, alineacion: e.target.value } })}
                                >
                                  <MenuItem value="left">Izquierda</MenuItem>
                                  <MenuItem value="center">Centro</MenuItem>
                                  <MenuItem value="right">Derecha</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <TextField autoComplete="off" label="X" size="small" type="text" inputMode="numeric" enterKeyHint="enter" value={selectedElement.x} onChange={(e) => updateSelectedElement({ x: Number(e.target.value) })} />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField autoComplete="off" label="Y" size="small" type="text" inputMode="numeric" enterKeyHint="enter" value={selectedElement.y} onChange={(e) => updateSelectedElement({ y: Number(e.target.value) })} />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField autoComplete="off" label="Ancho" size="small" type="text" inputMode="numeric" enterKeyHint="enter" value={selectedElement.ancho} onChange={(e) => updateSelectedElement({ ancho: Number(e.target.value) })} />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField autoComplete="off" label="Alto" size="small" type="text" inputMode="numeric" enterKeyHint="enter" value={selectedElement.alto} onChange={(e) => updateSelectedElement({ alto: Number(e.target.value) })} />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Tamaño</InputLabel>
                                <Select
                                  label="Tamaño"
                                  value={selectedElement.fuente.tamano}
                                  onChange={(e) => updateSelectedElement({ fuente: { ...selectedElement.fuente, tamano: e.target.value } })}
                                >
                                  <MenuItem value="chico">Chico</MenuItem>
                                  <MenuItem value="normal">Normal</MenuItem>
                                  <MenuItem value="grande">Grande</MenuItem>
                                  <MenuItem value="doble">Doble</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <Stack>
                                <FormControlLabel
                                  control={<Checkbox checked={selectedElement.visible} onChange={(e) => updateSelectedElement({ visible: e.target.checked })} />}
                                  label="Visible"
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedElement.fuente.negrita}
                                      onChange={(e) => updateSelectedElement({ fuente: { ...selectedElement.fuente, negrita: e.target.checked } })}
                                    />
                                  }
                                  label="Negrita"
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField autoComplete="off"
                                label="Contenido / token"
                                fullWidth
                                multiline
                                minRows={5}
                                value={selectedElement.texto}
                                onChange={(e) => updateSelectedElement({ texto: e.target.value })}
                                disabled={selectedElement.tipo === "imagen"}
                                onKeyDown={(e) => e.stopPropagation()}
                                sx={{ "& textarea": { fontFamily: "Consolas, monospace", fontSize: 13 } }}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 700 }}>JSON avanzado</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField autoComplete="off"
                          label="Plantilla JSON"
                          fullWidth
                          multiline
                          minRows={14}
                          value={draftTicket.plantillajson ?? ""}
                          onChange={(e) => setField("plantillajson", e.target.value)}
                          onBlur={onJsonBlur}
                          onKeyDown={(e) => e.stopPropagation()}
                          sx={{
                            "& textarea": {
                              fontFamily: "Consolas, monospace",
                              fontSize: 13,
                              lineHeight: 1.45,
                            },
                          }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vista previa ticket ventas</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              bgcolor: "#111",
              color: "#f5f5f5",
              borderRadius: 1,
              p: 2,
              m: 0,
              maxHeight: "70vh",
              overflow: "auto",
              fontFamily: "Consolas, monospace",
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
          >
            {previewText}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </SizeSoftXLayoutForm>
  );
}
